import { createEffect, createStore, sample } from 'effector';
import deepEqual from 'deep-equal';
import { cardUpdate, type RCardGet } from '../../../../../../apiRPC/card';
import {
  dynamicObjectCreate,
  dynamicObjectDelete,
  dynamicObjectGet,
  dynamicObjectUpdate,
  dynamicObjectSetValues,
} from '../../../../../../apiRPC/dynamicObject';
import { $card, resetCard } from '../../../../../model/card';
import { onOpenTaskOverview } from '../../../../../model/taskOverview';
import {
  ADDITIONAL_FIELDS_META,
  type AdditionalFieldKey,
  additionalFieldsSchemaNeedsUpdate,
  buildUnifiedDynamicObjectDefinition,
  parseAdditionalFieldsFromCard,
  preserveFieldsOutsideMeta,
  recalculateFinancialFields,
} from './model';

type AdditionalFieldsMigrationUiPatch = {
  cardUuid: string;
  mainDynamicObjectId: string | null;
  legacyDynamicObjectIds: string[];
};

const migrateAdditionalFieldsDynamicObjectsFx = createEffect<
  RCardGet,
  AdditionalFieldsMigrationUiPatch
>(async (card) => {
  const parsed = parseAdditionalFieldsFromCard(card);

  const initialValues = parsed.values;
  const mainDynamicObjectId = parsed.mainDynamicObjectId ?? null;
  const legacyDynamicObjectIds = parsed.legacyDynamicObjectIds;

  try {
    if (mainDynamicObjectId) {
      const existing = await dynamicObjectGet({ id: mainDynamicObjectId });
      const expectedKeys = Object.keys(
        ADDITIONAL_FIELDS_META,
      ) as AdditionalFieldKey[];
      const legacyValues = { ...initialValues };
      expectedKeys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(existing.values ?? {}, key)) {
          delete legacyValues[key];
        }
      });
      const currentValues = parseAdditionalFieldsFromCard({
        dynamicObjects: [existing],
      }).values;
      const migratedValues =
        legacyDynamicObjectIds.length > 0
          ? { ...currentValues, ...legacyValues }
          : currentValues;
      const currentDefinition =
        buildUnifiedDynamicObjectDefinition(currentValues);
      const nextDefinition = buildUnifiedDynamicObjectDefinition(
        recalculateFinancialFields(migratedValues),
      );
      const existingPropertyNames = new Set(
        (existing?.properties ?? []).map((p) => p.name),
      );

      const missingKeys = expectedKeys.filter(
        (key) => !existingPropertyNames.has(key),
      );

      const schemaNeedsUpdate =
        missingKeys.length > 0 ||
        additionalFieldsSchemaNeedsUpdate(existing?.properties ?? []);

      const updateObject = preserveFieldsOutsideMeta(nextDefinition, {
        properties: existing?.properties ?? [],
        values: existing?.values,
      });

      if (schemaNeedsUpdate) {
        // Обновляем схему: новые поля или изменённые enum-списки (участники ТМ/АМ/СМ и т.д.).
        await dynamicObjectUpdate({
          id: mainDynamicObjectId,
          fields: { object: updateObject },
        });
      }

      const changedValues = Object.fromEntries(
        Object.entries(nextDefinition.values ?? {}).filter(
          ([key, value]) =>
            !deepEqual(value, currentDefinition.values?.[key], {
              strict: true,
            }),
        ),
      );
      if (Object.keys(changedValues).length > 0) {
        await dynamicObjectSetValues({
          id: mainDynamicObjectId,
          values: changedValues,
        });
      }

      for (const id of legacyDynamicObjectIds) {
        if (!id || id === mainDynamicObjectId) continue;
        await dynamicObjectDelete({ id });
      }

      return {
        cardUuid: card.uuid,
        mainDynamicObjectId,
        legacyDynamicObjectIds: [],
      };
    }

    if (legacyDynamicObjectIds.length === 0) {
      return {
        cardUuid: card.uuid,
        mainDynamicObjectId: null,
        legacyDynamicObjectIds: [],
      };
    }

    const created = await dynamicObjectCreate({
      fields: {
        object: buildUnifiedDynamicObjectDefinition(
          recalculateFinancialFields(initialValues),
        ),
      },
    });

    await cardUpdate({
      uuid: card.uuid,
      fields: { dynamicObjects: { add: [created.id] } },
    });

    for (const id of legacyDynamicObjectIds) {
      if (!id || id === created.id) continue;
      await dynamicObjectDelete({ id });
    }

    return {
      cardUuid: card.uuid,
      mainDynamicObjectId: created.id,
      legacyDynamicObjectIds: [],
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      'Failed to migrate dynamic objects for AdditionalFields',
      error,
    );
    return {
      cardUuid: card.uuid,
      mainDynamicObjectId,
      legacyDynamicObjectIds,
    };
  }
});

export const $additionalFieldsMigrationUiPatch =
  createStore<AdditionalFieldsMigrationUiPatch | null>(null)
    .on(migrateAdditionalFieldsDynamicObjectsFx.doneData, (_, patch) => patch)
    .reset(resetCard);

sample({
  clock: onOpenTaskOverview,
  source: $card,
  filter: (card, isOpen): card is RCardGet =>
    isOpen === true && Boolean(card?.uuid),
  fn: (card): RCardGet => {
    if (!card) {
      throw new Error('additionalFieldsMigration: empty card after filter');
    }
    return card;
  },
  target: migrateAdditionalFieldsDynamicObjectsFx,
});
